import { TXN_PANEL_DIALOG_CLASS } from "@app/components/txn-table-panel";
import { useToast } from "@app/components/toast";
import { getAuthToken } from "@app/relay/fetch-relay";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import SeatHistoryChart from "./seat-history-chart";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { diligenceKpiCellPanel_Query } from "@app/queries/diligenceKpiCellPanel_Query.graphql";

// What a clicked matrix cell drills into: one metric, one segment, one basis,
// one column.
export type DiligenceKpiCellScope = {
  metric: string;
  label: string;
  month: string;
  basis: "ttm" | "mom";
  dimension: string;
  band: string;
  segmentLabel: string;
  // What the table printed. The panel re-derives the same number from the rows
  // it lists and shows both, so a disagreement is visible rather than silent.
  cellValue?: number | null;
};

const PAGE_SIZE = 500;

const cellQuery = graphql`
  query diligenceKpiCellPanel_Query(
    $metric: String!
    $month: String!
    $basis: String
    $dimension: String
    $band: String
    $limit: Int
    $offset: Int
  ) {
    diligenceCell(
      metric: $metric
      month: $month
      basis: $basis
      dimension: $dimension
      band: $band
      limit: $limit
      offset: $offset
    ) {
      metric
      label
      format
      basis
      month
      windowFrom
      windowTo
      formula
      note
      value
      totalCount
      limit
      offset
      components {
        label
        value
        format
        metric
        month
        basis
      }
      derivation {
        formula
        result
        undefined
        operands {
          label
          value
          format
          metric
          month
          basis
        }
      }
      methodology {
        label
        text
      }
      notSegmentable {
        reason
        supportedDimensions
      }
      columns {
        key
        label
        format
        align
      }
      rows {
        pseudonym
        cells {
          number
          text
        }
      }
      totals {
        number
        text
      }
    }
  }
`;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const monthName = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

function fmt(
  value: {
    readonly number?: number | null;
    readonly text?: string | null;
  },
  format: string
): string {
  if (value.text != null)
    return format === "month" ? monthName(value.text) : value.text;
  if (value.number == null) return "";
  switch (format) {
    case "money":
      return usd.format(value.number);
    case "percent":
      return `${(value.number * 100).toFixed(1)}%`;
    case "int":
      return value.number.toLocaleString();
    default:
      return value.number.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
  }
}

const fmtScalar = (value: number | null | undefined, format: string) =>
  value == null ? "—" : fmt({ number: value, text: null }, format);

export default function DiligenceKpiCellPanel({
  scope,
  onClose,
  refreshToken = 0,
}: {
  scope: DiligenceKpiCellScope;
  onClose: () => void;
  // Bumped by the page when a matrix run completes. The drill payload must
  // never be OLDER than the snapshot it is compared against, or the stale
  // banner would blame the wrong side.
  refreshToken?: number;
}) {
  // A trail, not a single scope: a ratio's terms are themselves cells, so
  // clicking the denominator of a churn number walks INTO it and Back walks out.
  const [trail, setTrail] = useState<DiligenceKpiCellScope[]>([scope]);
  const current = trail[trail.length - 1];

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20" />
        <Dialog.Content
          className={TXN_PANEL_DIALOG_CLASS}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">{current.label}</Dialog.Title>
          <Suspense
            fallback={
              <div className="p-6 text-sm text-gray-400">
                Working out what this number is made of…
              </div>
            }
          >
            <PanelContent
              scope={current}
              refreshToken={refreshToken}
              canGoBack={trail.length > 1}
              onBack={() => setTrail((prev) => prev.slice(0, -1))}
              onWalkInto={(next) => setTrail((prev) => [...prev, next])}
              onClose={onClose}
            />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelContent({
  scope,
  refreshToken,
  canGoBack,
  onBack,
  onWalkInto,
  onClose,
}: {
  scope: DiligenceKpiCellScope;
  refreshToken: number;
  canGoBack: boolean;
  onBack: () => void;
  onWalkInto: (scope: DiligenceKpiCellScope) => void;
  onClose: () => void;
}) {
  const [page, setPage] = useState(0);
  // A customer whose seat curve is pinned open above the table. The cohort stays
  // visible below it — the point is to check one row against the group.
  const [seatCustomer, setSeatCustomer] = useState<string | null>(null);
  // The methodology is a paragraph per clause; the header is already dense, so
  // it opens on demand.
  const [showMethodology, setShowMethodology] = useState(false);

  // Policy follows the key: a fetchKey bump under store-or-network sends NO
  // request when the store already holds the payload, so a refresh must also
  // switch to network-only (the repo's documented fetchKey trap).
  const [initialToken] = useState(refreshToken);
  const data = useLazyLoadQuery<diligenceKpiCellPanel_Query>(
    cellQuery,
    {
      metric: scope.metric,
      month: scope.month,
      basis: scope.basis,
      dimension: scope.dimension,
      band: scope.band,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    {
      fetchPolicy:
        refreshToken === initialToken ? "store-or-network" : "network-only",
      fetchKey: refreshToken,
    }
  );

  const cell = data.diligenceCell;
  const pageCount = Math.max(1, Math.ceil(cell.totalCount / cell.limit));
  const paged = cell.totalCount > cell.rows.length;
  const window =
    cell.windowFrom === cell.windowTo
      ? monthName(cell.windowTo)
      : `${monthName(cell.windowFrom)} – ${monthName(cell.windowTo)}`;

  // Raw-value comparison at the precision each format is stored with: counts
  // are whole, money is rounded to cents, ratios to six places.
  const tolerance =
    cell.format === "percent" ? 0.000002 : cell.format === "int" ? 0.5 : 0.005;
  // Two different disagreements, two different meanings. The snapshot lagging
  // the live drill is staleness (regenerate the report); the derivation footer
  // failing to reproduce the live value would be a bug in this feature. A cell
  // that is undefined on BOTH sides is quietly undefined — but undefined on
  // one side only is exactly the silent disagreement the banner exists for.
  const compared = scope.cellValue !== undefined;
  const snapshotStale =
    compared &&
    ((scope.cellValue == null) !== (cell.value == null) ||
      (scope.cellValue != null &&
        cell.value != null &&
        Math.abs(scope.cellValue - cell.value) > tolerance));
  const derivationBroken =
    cell.derivation != null &&
    ((cell.derivation.result == null) !== (cell.value == null) ||
      (cell.derivation.result != null &&
        cell.value != null &&
        cell.derivation.result !== cell.value));

  if (cell.notSegmentable) {
    return (
      <>
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {canGoBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-50"
                >
                  ← Back
                </button>
              )}
              <h2 className="truncate text-sm font-semibold text-gray-900">
                {cell.label}
              </h2>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">{scope.segmentLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm font-medium text-gray-900">
            This number is not segmented that way.
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {cell.notSegmentable.reason}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Available for:{" "}
            {cell.notSegmentable.supportedDimensions
              .map((dim) =>
                dim === "overall" ? "the whole book" : `by ${dim} band`
              )
              .join(", ")}
            .
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {canGoBack && (
              <button
                type="button"
                onClick={onBack}
                className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-50"
              >
                ← Back
              </button>
            )}
            <h2 className="truncate text-sm font-semibold text-gray-900">
              {cell.label}
            </h2>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            {scope.segmentLabel} ·{" "}
            {cell.basis === "ttm" ? "trailing 12 months" : "month over month"} ·{" "}
            {window} · {cell.totalCount.toLocaleString()} row
            {cell.totalCount === 1 ? "" : "s"}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            <span className="font-medium">
              These rows come to {fmtScalar(cell.value, cell.format)}
            </span>
            {scope.cellValue != null && (
              <span className={snapshotStale ? "text-amber-700" : "text-gray-400"}>
                {" "}
                · the table reads {fmtScalar(scope.cellValue, cell.format)}
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-gray-400">{cell.formula}</p>
          {cell.methodology.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMethodology((open) => !open)}
              className="mt-1 text-xs text-blue-700 underline decoration-dotted underline-offset-4 hover:text-blue-900"
            >
              {showMethodology ? "Hide methodology" : "Methodology"}
            </button>
          )}
          {derivationBroken && (
            <p className="mt-1 text-xs font-medium text-red-700">
              The derivation footer does not reproduce the number above. That is
              not stale data — it is a defect in this panel's arithmetic, and
              the cell should not be trusted until it is fixed.
            </p>
          )}
          {snapshotStale && !derivationBroken && (
            <p className="mt-1 text-xs text-amber-700">
              These rows come to{" "}
              {cell.value == null ? "undefined" : fmtScalar(cell.value, cell.format)}{" "}
              but the stored report reads{" "}
              {scope.cellValue == null
                ? "undefined"
                : fmtScalar(scope.cellValue, cell.format)}
              . This list is computed now; the report is the stored snapshot, so
              the report is out of date — generate it again.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ExportCellButton scope={scope} />
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {showMethodology && (
        <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
          <dl className="space-y-2">
            {cell.methodology.map((entry) => (
              <div key={entry.label}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {entry.label}
                </dt>
                <dd className="mt-0.5 text-xs leading-relaxed text-gray-600">
                  {entry.text}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {cell.components.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50 px-5 py-2">
          {cell.components.map((component, index) => {
            const walkable =
              component.metric != null && component.month != null;
            return (
              <button
                key={`${component.label}-${index}`}
                type="button"
                disabled={!walkable}
                onClick={() =>
                  walkable &&
                  onWalkInto({
                    metric: component.metric as string,
                    label: component.label,
                    month: component.month as string,
                    basis: (component.basis as "ttm" | "mom") ?? scope.basis,
                    dimension: scope.dimension,
                    band: scope.band,
                    segmentLabel: scope.segmentLabel,
                    cellValue: component.value,
                  })
                }
                title={
                  walkable
                    ? `Show what ${component.label} is made of`
                    : undefined
                }
                className={`rounded border px-2 py-1 text-xs ${
                  walkable
                    ? "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:text-blue-700"
                    : "border-transparent text-gray-500"
                }`}
              >
                <span className="text-gray-500">{component.label}</span>{" "}
                <span className="font-mono tabular-nums">
                  {fmtScalar(component.value, component.format)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {seatCustomer && (
          <Suspense
            fallback={
              <div className="border-b border-gray-200 px-5 py-4 text-sm text-gray-400">
                Loading the seat history…
              </div>
            }
          >
            <SeatHistoryChart
              pseudonym={seatCustomer}
              onClose={() => setSeatCustomer(null)}
            />
          </Suspense>
        )}
        {cell.rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            Nothing sits behind this number — it is zero because there was
            nothing to count.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                {cell.columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 ${
                      column.align === "right" ? "text-right" : "text-left"
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cell.rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  {row.cells.map((value, index) => {
                    const column = cell.columns[index];
                    const negative =
                      (value.number ?? 0) < 0 && column.format === "money";
                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-1.5 ${
                          column.align === "right"
                            ? "whitespace-nowrap text-right font-mono tabular-nums"
                            : "text-gray-700"
                        } ${negative ? "text-red-600" : ""}`}
                      >
                        {column.key === "pseudonym" &&
                        row.pseudonym != null ? (
                          <button
                            type="button"
                            onClick={() =>
                              setSeatCustomer(row.pseudonym as string)
                            }
                            title="Show this customer's seat count over time"
                            className="rounded underline decoration-dotted decoration-gray-300 underline-offset-4 hover:text-blue-700 hover:decoration-blue-400"
                          >
                            {fmt(value, column.format)}
                          </button>
                        ) : (
                          fmt(value, column.format)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-gray-50">
              <tr className="border-t border-gray-300 font-semibold text-gray-900">
                {cell.totals.map((value, index) => {
                  const column = cell.columns[index];
                  return (
                    <td
                      key={column.key}
                      className={`px-3 py-2 ${
                        column.align === "right"
                          ? "whitespace-nowrap text-right font-mono tabular-nums"
                          : "text-xs uppercase tracking-wide text-gray-500"
                      }`}
                    >
                      {index === 0 && value.text == null && value.number == null
                        ? paged
                          ? "All rows"
                          : "Total"
                        : fmt(value, column.format)}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {cell.derivation && (
        <div className="border-t border-gray-200 bg-gray-50 px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            How this number is derived
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {cell.derivation.operands.map((operand, index) => {
              const walkable = operand.metric != null && operand.month != null;
              return (
                <button
                  key={`${operand.label}-${index}`}
                  type="button"
                  disabled={!walkable}
                  onClick={() =>
                    walkable &&
                    onWalkInto({
                      metric: operand.metric as string,
                      label: operand.label,
                      month: operand.month as string,
                      basis: (operand.basis as "ttm" | "mom") ?? scope.basis,
                      dimension: scope.dimension,
                      band: scope.band,
                      segmentLabel: scope.segmentLabel,
                      cellValue: operand.value,
                    })
                  }
                  title={
                    walkable
                      ? `Show what ${operand.label} is made of`
                      : undefined
                  }
                  className={`rounded border px-2 py-1 text-xs ${
                    walkable
                      ? "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:text-blue-700"
                      : "border-transparent bg-white text-gray-600"
                  }`}
                >
                  <span className="text-gray-500">{operand.label}</span>{" "}
                  <span className="font-mono tabular-nums">
                    {fmtScalar(operand.value, operand.format)}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-gray-500">
            {cell.derivation.formula}
          </p>
          <p className="mt-0.5 text-xs font-medium text-gray-900">
            {cell.derivation.undefined
              ? "= undefined (the denominator is empty, so there is no rate to state)"
              : `= ${fmtScalar(cell.derivation.result, cell.format)}`}
          </p>
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3 text-xs text-gray-500">
          <span>
            Rows {page * cell.limit + 1}–
            {Math.min((page + 1) * cell.limit, cell.totalCount)} of{" "}
            {cell.totalCount.toLocaleString()} · the totals row covers all of
            them
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Downloads the COMPLETE cell from the server — every constituent row, the
// derivation and the methodology — never the 500-row page on screen. The JWT
// lives in localStorage, so a plain <a href> cannot carry it: fetch with the
// bearer header, then a blob download. The recompute can take a while on a
// TTM cell, so the button says so instead of looking dead.
function ExportCellButton({ scope }: { scope: DiligenceKpiCellScope }) {
  const notify = useToast();
  const [exporting, setExporting] = useState(false);

  const exportCell = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        metric: scope.metric,
        month: scope.month,
        basis: scope.basis,
        dimension: scope.dimension,
        band: scope.band,
      }).toString();
      const response = await fetch(`/exports/diligence_cell?${qs}`, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ""}` },
      });
      if (response.status === 401)
        return notify({ message: "Session expired — sign in again.", kind: "error" });
      if (response.status === 403)
        return notify({ message: "Your data isn't revealed yet.", kind: "error" });
      if (response.status === 422) {
        const body = (await response.json()) as { error?: string };
        return notify({
          message: body.error ?? "This cell cannot be exported.",
          kind: "error",
        });
      }
      if (!response.ok)
        return notify({ message: "Export failed. Please try again.", kind: "error" });

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${scope.metric}-${scope.band}-${scope.month}-${scope.basis}`.replace(
        /[^a-zA-Z0-9-]+/g,
        "-"
      );
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      notify({ message: "Export failed. Please try again.", kind: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      disabled={exporting}
      onClick={exportCell}
      title="Download every row behind this number, with its derivation and methodology"
      className="rounded border px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
    >
      {exporting ? "Exporting the whole cell…" : "Export"}
    </button>
  );
}
